/**
 * Pagos: la bandeja de comprobantes y la cartera abierta.
 *
 * Confirmar un comprobante es el camino más delicado del sistema —registra el
 * pago, salda el cobro, asienta el ingreso y pide la factura, todo o nada— y por
 * eso la pantalla no ofrece atajos: se ve el comprobante, se confirma o se
 * rechaza con motivo.
 *
 * ── CB-12 · Por qué dos pestañas y no tres ─────────────────────────────────
 *
 * Eran «Por revisar», «Pendientes» y «Vencidos». Las dos últimas mostraban 66
 * filas cada una y **diferían en una fila en cada dirección**, porque 65 de
 * los 66 pendientes ya estaban vencidos. Dos pestañas con el mismo contenido
 * no son dos vistas: enseñan a desconfiar de las dos.
 *
 * Lo que sí distingue a un cobro de otro es **la acción que exige**:
 *
 *   Por revisar  no se sabe si el dinero entró.  Acción: investigar.
 *   Abiertos     se sabe que no está pago.       Acción: cobrar.
 *
 * «Vencido» dejó de ser pestaña y pasó a ser **orden y filtro por antigüedad**
 * —0-30 / 31-60 / 61-90 / +90— dentro de «Abiertos». Da lo que la pestaña
 * prometía, graduado en vez de binario: un cobro de 100 días y uno de 6 no
 * piden la misma conversación, y una bandera no los distinguía.
 *
 * Y «Abiertos» incluye los tres estados abiertos —`PENDING`,
 * `PARTIALLY_PAID`, `UNDER_REVIEW`—, no solo el primero. Con el filtro por
 * estado exacto, el pago parcial vencido de diciembre 2025 aparecía en
 * «Vencidos» y no en «Pendientes», aunque obviamente estaba pendiente de
 * completarse.
 */

import { Fragment, useState } from 'react';
import {
  dateLabel, PAYMENT_METHOD_LABEL, PAYMENT_STATUS_LABEL, PAYMENT_STATUS_TONE, periodLabel,
  today, type PaymentMethod, type PaymentStatus, type Tone,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  colonDigits, colonDigitsToMinor, formatColonDigits, minorToColonDigits,
} from '@/lib/paymentAmount';
import {
  Async, Badge, Button, Card, Empty, Field, Modal, Money, Note, PageHead, StatusBadge, Table,
} from '@/components/ui';
import '@/styles/cobros.css';

interface Submission {
  id: string; charge_payer_id: string; declared_amount_minor: number;
  declared_method: PaymentMethod; declared_reference: string | null; declared_paid_on: string | null;
  status: string; created_at: string; due_minor: number;
  billing_name_snapshot: string; unit_code: string; period_start: string;
  submitted_by_name: string | null;
}

/** Lo que hace falta para explicar un vacío sin inventarle una causa. */
interface InboxContext {
  submissions_ever: number;
  client_profiles: number;
  client_logins: number;
}

type AgingBand = 'AL_DIA' | 'D30' | 'D60' | 'D90' | 'D90_MAS';

interface Receivable {
  id: string; billing_name_snapshot: string; unit_code: string; period_start: string;
  due_minor: number; paid_minor: number; due_date: string; payment_status: PaymentStatus;
  open_submissions: number; display_name: string;
  /** Días desde el vencimiento. Negativo si todavía no vence. Lo calcula el Worker. */
  age_days: number;
  aging_band: AgingBand;
}

/**
 * Los tramos, en el orden en que se leen: primero lo más viejo, que es lo que
 * más urge. `AL_DIA` va al final porque no es un tramo de antigüedad sino su
 * ausencia — el cobro está abierto y todavía no vence.
 */
const AGING_BANDS: { key: AgingBand; label: string; tone: Tone }[] = [
  { key: 'D90_MAS', label: 'Más de 90 días', tone: 'danger' },
  { key: 'D90', label: '61 a 90 días', tone: 'danger' },
  { key: 'D60', label: '31 a 60 días', tone: 'warn' },
  { key: 'D30', label: 'Hasta 30 días', tone: 'warn' },
  { key: 'AL_DIA', label: 'Todavía no vence', tone: 'ok' },
];

const BAND_LABEL: Record<AgingBand, string> =
  Object.fromEntries(AGING_BANDS.map((b) => [b.key, b.label])) as Record<AgingBand, string>;

export function PaymentsPage() {
  const [tab, setTab] = useState<'inbox' | 'open'>('inbox');
  const [band, setBand] = useState<AgingBand | 'ALL'>('ALL');

  const submissions = useApi<{ submissions: Submission[]; context: InboxContext }>('/admin/submissions');
  const receivables = useApi<{ receivables: Receivable[] }>(
    '/admin/receivables?open=1', { enabled: tab === 'open' },
  );

  const pending = submissions.data?.submissions.length ?? 0;

  return (
    <>
      <PageHead
        title="Pagos"
        subtitle="Comprobantes por revisar y cuentas por cobrar abiertas, de la más vieja a la más nueva."
      />

      <div className="tabs mb-2">
        <button type="button" className={`tab${tab === 'inbox' ? ' is-active' : ''}`} aria-pressed={tab === 'inbox'} onClick={() => { if (tab !== 'inbox') { haptic.light(); setTab('inbox'); } }}> {/* HAPTIC_FEEDBACK */}
          Por revisar {pending > 0 && `(${pending})`}
        </button>
        <button type="button" className={`tab${tab === 'open' ? ' is-active' : ''}`} aria-pressed={tab === 'open'} onClick={() => { if (tab !== 'open') { haptic.light(); setTab('open'); } }}> {/* HAPTIC_FEEDBACK */}
          Abiertos
        </button>
      </div>

      {tab === 'inbox' ? (
        <Async query={submissions} empty={(d) => d.submissions.length === 0 && (
          <InboxEmpty context={d.context} />
        )}>
          {(data) => (
            <div style={{ display: 'grid', gap: 12 }}>
              {data.submissions.map((s) => <SubmissionCard key={s.id} submission={s} />)}
            </div>
          )}
        </Async>
      ) : (
        <Async query={receivables} empty={(d) => d.receivables.length === 0 && (
          <Empty
            icon="confirmar"
            title="No queda cartera abierta"
            hint="Ningún cobro emitido está pendiente, parcial ni esperando revisión."
          />
        )}>
          {(data) => (
            <OpenReceivables rows={data.receivables} band={band} onBand={setBand} />
          )}
        </Async>
      )}
    </>
  );
}

/**
 * El vacío de la bandeja, explicado por la causa real.
 *
 * Decía «Todo revisado», y eso era falso: nunca llegó un comprobante. Los sube
 * el cliente desde su portal, y si ningún cliente tiene acceso la bandeja está
 * vacía por construcción. La frase se arma con dos números medidos —cuántos
 * comprobantes entraron alguna vez y cuántos clientes pueden entrar— así que
 * cuando el mundo cambie, el texto cambia solo.
 */
function InboxEmpty({ context }: { context: InboxContext }) {
  const { submissions_ever: ever, client_profiles: profiles, client_logins: logins } = context;

  if (ever === 0 && logins === 0) {
    return (
      <Empty
        icon="horas"
        title="Nunca llegó un comprobante"
        hint={
          <>
            No es que estén todos revisados: no hay ninguno. Los comprobantes los sube el cliente
            desde su portal, y hoy {profiles === 0 ? 'no hay perfiles de cliente cargados'
              : `ninguno de los ${profiles} perfiles de cliente tiene acceso`}.
          </>
        }
      />
    );
  }

  if (ever === 0) {
    return (
      <Empty
        icon="horas"
        title="Nunca llegó un comprobante"
        hint={`No es que estén todos revisados: no hay ninguno. ${logins} de ${profiles} ${profiles === 1 ? 'cliente' : 'clientes'} ${logins === 1 ? 'ya puede' : 'ya pueden'} entrar al portal y subirlos.`}
      />
    );
  }

  return (
    <Empty
      icon="confirmar"
      title="No hay comprobantes esperando"
      hint={ever === 1 ? 'El único que llegó ya se revisó.' : `Los ${ever} que llegaron ya se revisaron.`}
    />
  );
}

/**
 * Una sola lista de cartera abierta, con sus cortes por antigüedad.
 *
 * El filtro es el mismo `.segmented` del resto del admin y sus contadores
 * salen de las filas que ya están en pantalla — no hay una segunda consulta
 * que pueda contar distinto de lo que se ve.
 */
function OpenReceivables({ rows, band, onBand }: {
  rows: Receivable[]; band: AgingBand | 'ALL'; onBand: (b: AgingBand | 'ALL') => void;
}) {
  const counts = AGING_BANDS.map((b) => ({
    ...b, count: rows.filter((r) => r.aging_band === b.key).length,
  }));
  // Un tramo sin filas no se ofrece: un filtro que lleva a una tabla vacía es
  // un callejón, y el contador en cero ya lo dice desde el total.
  const options = [
    { key: 'ALL' as const, label: 'Todos', tone: 'neutral' as Tone, count: rows.length },
    ...counts.filter((b) => b.count > 0),
  ];
  const shown = band === 'ALL' ? rows : rows.filter((r) => r.aging_band === band);
  const outstanding = shown.reduce((sum, r) => sum + (r.due_minor - r.paid_minor), 0);

  return (
    <>
      <div className="row row--wrap mb-2">
        <div
          className="segmented"
          role="group"
          aria-label="Filtrar por antigüedad"
          style={{
            '--seg-n': options.length,
            '--seg-i': Math.max(options.findIndex((o) => o.key === band), 0),
          } as React.CSSProperties}
        >
          <div className="segmented__pill" />
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              aria-pressed={band === o.key}
              className={`segmented__opt segmented__opt--${o.tone}${band === o.key ? ' is-active' : ''}`}
              onClick={() => { if (band !== o.key) haptic.light(); onBand(o.key); }} // HAPTIC_FEEDBACK
            >
              {o.label}
              <span className="segmented__count">{o.count}</span>
            </button>
          ))}
        </div>

        <span className="spacer" />
        <span className="small muted">
          {shown.length} {shown.length === 1 ? 'cobro' : 'cobros'} · <Money value={outstanding} /> por cobrar
        </span>
      </div>

      <ReceivableTable rows={shown} showBands={band === 'ALL'} />
    </>
  );
}

function SubmissionCard({ submission: s }: { submission: Submission }) {
  const [rejecting, setRejecting] = useState(false);
  const short = s.declared_amount_minor < s.due_minor;

  const confirm = useApiMutation(
    () => api.post(`/admin/submissions/${s.id}/confirm`, {}),
    ['/admin/submissions', '/admin/receivables', '/admin/dashboard', '/admin/charges'],
    { onSuccess: () => haptic.success() }, // HAPTIC_FEEDBACK
  );

  return (
    <Card
      title={
        <div>
          <h2>{s.billing_name_snapshot}</h2>
          <span className="small muted">
            {s.unit_code} · {periodLabel(s.period_start)}
            {s.submitted_by_name && ` · lo subió ${s.submitted_by_name}`}
          </span>
        </div>
      }
      actions={
        <>
          <Button onClick={() => setRejecting(true)}>Rechazar</Button>
          <Button variant="primary" disabled={confirm.isPending} onClick={() => confirm.mutate()}>
            {confirm.isPending ? 'Confirmando…' : 'Confirmar pago'}
          </Button>
        </>
      }
    >
      {confirm.error && <Note tone="danger">{confirm.error.message}</Note>}

      <div className="grid grid--2">
        <div>
          <dl className="dl">
            <dt>Declara haber pagado</dt><dd><Money value={s.declared_amount_minor} /></dd>
            <dt>El cobro es de</dt><dd><Money value={s.due_minor} /></dd>
            <dt>Método</dt><dd>{PAYMENT_METHOD_LABEL[s.declared_method]}</dd>
            <dt>Fecha declarada</dt>
            <dd>{s.declared_paid_on ? dateLabel(s.declared_paid_on) : '—'}</dd>
            <dt>Referencia</dt><dd>{s.declared_reference ?? '—'}</dd>
          </dl>

          {short && (
            <div className="mt-2">
              <Note tone="warn">
                Declara menos de lo que debe. Al confirmar queda como pago parcial y el saldo sigue abierto.
              </Note>
            </div>
          )}
        </div>

        <div>
          {/* El comprobante vive en un bucket privado; lo sirve el Worker. */}
          <a href={`/api/admin/submissions/${s.id}/receipt`} target="_blank" rel="noreferrer">
            <img
              src={`/api/admin/submissions/${s.id}/receipt`}
              alt="Comprobante"
              style={{
                maxWidth: '100%', maxHeight: 260, borderRadius: 8,
                border: '1px solid var(--line)', display: 'block',
              }}
            />
          </a>
          <p className="small muted mt-1">Tocá la imagen para verla completa.</p>
        </div>
      </div>

      {rejecting && <RejectModal submissionId={s.id} onClose={() => setRejecting(false)} />}
    </Card>
  );
}

function RejectModal({ submissionId, onClose }: { submissionId: string; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const mutation = useApiMutation(
    () => api.post(`/admin/submissions/${submissionId}/reject`, { reason }),
    ['/admin/submissions', '/admin/receivables', '/admin/dashboard'],
    { onSuccess: () => { haptic.warning(); onClose(); } }, // HAPTIC_FEEDBACK
  );

  return (
    <Modal
      title="Rechazar el comprobante"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="danger" disabled={reason.length < 3 || mutation.isPending}
            onClick={() => mutation.mutate()}>
            Rechazar
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Field label="Motivo" required hint="El cliente lo va a leer en su portal, así que sé claro.">
        <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

/**
 * La cartera, en una tabla.
 *
 * `showBands` inserta los cortes de antigüedad como filas separadoras. Solo
 * tiene sentido cuando se están viendo todos los tramos: filtrado a uno, el
 * encabezado repetiría lo que ya dice el filtro.
 *
 * La antigüedad se muestra **en días, siempre**, y no solo como color: es el
 * dato con el que se decide a quién llamar primero.
 */
function ReceivableTable({ rows, showBands = false }: { rows: Receivable[]; showBands?: boolean }) {
  const [paying, setPaying] = useState<Receivable | null>(null);
  const now = today();
  let lastBand: AgingBand | null = null;

  return (
    <>
      <Card flush>
        <Table head={
          <tr>
            <th>Pagador</th><th>Consultorio</th><th>Período</th>
            <th className="right">Debe</th><th>Vence</th><th className="right">Antigüedad</th>
            <th>Estado</th><th />
          </tr>
        }>
          {rows.map((r) => {
            const openBand = showBands && r.aging_band !== lastBand ? r.aging_band : null;
            lastBand = r.aging_band;
            const tone = AGING_BANDS.find((b) => b.key === r.aging_band)?.tone ?? 'neutral';

            return (
              <Fragment key={r.id}>
                {openBand && (
                  <tr className="aging__band">
                    <td colSpan={8}>
                      <span className={`aging__dot aging__dot--${tone}`} aria-hidden="true" />
                      {BAND_LABEL[openBand]}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="strong">{r.billing_name_snapshot}</td>
                  <td>{r.unit_code}</td>
                  <td className="small muted">{periodLabel(r.period_start)}</td>
                  <td className="right"><Money value={r.due_minor - r.paid_minor} /></td>
                  <td className="small">
                    {r.due_date < now
                      ? <Badge tone="danger">{dateLabel(r.due_date)}</Badge>
                      : <span className="muted">{dateLabel(r.due_date)}</span>}
                  </td>
                  <td className="right small">
                    {r.age_days > 0
                      ? <span style={{ color: `var(--${tone === 'danger' ? 'danger' : tone === 'warn' ? 'warn' : 'ink-2'})` }}>
                        {r.age_days} d
                      </span>
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    <StatusBadge value={r.payment_status} labels={PAYMENT_STATUS_LABEL} tones={PAYMENT_STATUS_TONE} />
                  </td>
                  <td className="right">
                    <Button onClick={() => setPaying(r)}>Registrar pago</Button>
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </Table>
      </Card>

      {paying && <ManualPaymentModal receivable={paying} onClose={() => setPaying(null)} />}
    </>
  );
}

/**
 * Pago registrado por el administrador, sin comprobante previo.
 *
 * Pasa por exactamente la misma cadena que un comprobante confirmado: ledger,
 * factura, bitácora. Un segundo camino hacia el ingreso sería un segundo lugar
 * donde equivocarse.
 */
export interface ManualPaymentTarget {
  id: string; billing_name_snapshot: string; due_minor: number; paid_minor: number;
}

export function ManualPaymentModal({ receivable, onClose }: { receivable: ManualPaymentTarget; onClose: () => void }) {
  const [amount, setAmount] = useState(
    minorToColonDigits(receivable.due_minor - receivable.paid_minor),
  );
  const [method, setMethod] = useState<PaymentMethod>('SINPE_MOBILE');
  const [date, setDate] = useState(today());
  const [reference, setReference] = useState('');
  const amountMinor = colonDigitsToMinor(amount);

  const mutation = useApiMutation(
    () => api.post('/admin/payments/manual', {
      charge_payer_id: receivable.id,
      amount: amountMinor,
      paid_at: new Date(`${date}T12:00:00Z`).toISOString(),
      method,
      reference: reference || undefined,
    }),
    ['/admin/receivables', '/admin/dashboard', '/admin/charges', '/admin/payments', '/admin/matrix', '/admin/units'],
    { onSuccess: () => { haptic.success(); onClose(); } }, // HAPTIC_FEEDBACK
  );

  return (
    <Modal
      title={`Registrar pago · ${receivable.billing_name_snapshot}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!amountMinor || !date || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Registrando…' : 'Registrar'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Monto (colones)" required
        hint={<>Saldo pendiente: <Money value={receivable.due_minor - receivable.paid_minor} />.</>}>
        <input className="input input--money" type="text" inputMode="numeric"
          placeholder="0" value={formatColonDigits(amount)}
          onChange={(e) => setAmount(colonDigits(e.target.value))} />
      </Field>

      <Field label="Método" required>
        <select className="select" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
          {(Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[]).map((m) => (
            <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m]}</option>
          ))}
        </select>
      </Field>

      <Field label="Fecha del pago" required>
        <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>

      <Field label="Referencia">
        <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} />
      </Field>
    </Modal>
  );
}

export { ReceivableTable };
